import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { compact, keyBy } from 'lodash'
import type {
  OpsCall,
  VoiceAgentOpsCounts,
  VoiceAgentOpsLocationRow,
} from 'app/components/ai-agent-voice-ops/voiceAgentOps.types'
import {
  CALL_FAILURE_GROUP,
  CALL_FAILURE_GROUP_COLOR,
  CALL_FAILURE_LABEL,
  METRIC_DEFINITIONS,
  TOOL_FLAG_COLOR,
  TOOL_FLAG_LABEL,
  TOOL_FLAG_METRIC_LABEL,
  noBookingReasonLabel,
  ratesFor,
  totalCounts,
  type VoiceAgentOpsRates,
} from 'app/components/ai-agent-voice-ops/voiceAgentOpsMetrics'
import { DateComponent } from 'app/components/DateComponent'
import MetricCell from 'app/components/table/MetricCell'
import { OverflowCountChip } from 'app/components/shared/OverflowCountChip'
import { Badge, type BadgeColor } from 'app/components/ui/badge'
import { defaultColumns } from 'app/table-columns/activities'
import type { ActivityJson } from 'app/types/entities'

type LocationColumn = ColumnDef<VoiceAgentOpsLocationRow, any>
type CallColumn = ColumnDef<OpsCall, any>
type PercentRate = Exclude<keyof VoiceAgentOpsRates, `scheduleRateChange`>

// Failure rates sit in the low single digits, so they keep a decimal; schedule rates
// stay whole percents like the Dashboard tab.
const RATE_DECIMALS: Record<PercentRate, number> = { scheduleRate: 0, previousScheduleRate: 0, failureRate: 1 }

/** Pinned Location column (clicking filters the page to it), plus Workspace at org level. */
function locationColumns(
  showWorkspace: boolean,
  onLocationClick: (row: VoiceAgentOpsLocationRow) => void,
  footer?: string,
): LocationColumn[] {
  return compact([
    {
      accessorFn: (row) => row.locationName,
      id: `location`,
      header: `Location`,
      enableHiding: false,
      enableSorting: true,
      meta: { defaultVisible: true, pinned: true, onClick: onLocationClick },
      footer,
    },
    showWorkspace && {
      accessorFn: (row) => row.workspaceName,
      id: `workspace`,
      header: `Workspace`,
      enableSorting: true,
      meta: { defaultVisible: true },
    },
  ])
}

function countColumn(id: Exclude<keyof VoiceAgentOpsCounts, `failedCalls`>, header: string): LocationColumn {
  return {
    accessorKey: id,
    id,
    header,
    enableSorting: true,
    cell: (props) => <MetricCell value={props.getValue()} />,
    meta: { defaultVisible: true, infoTooltip: { title: header, message: METRIC_DEFINITIONS[id] } },
    footer: ({ table }) => (
      <MetricCell value={totalCounts(table.getFilteredRowModel().rows.map((row) => row.original))[id]} />
    ),
  }
}

function rateColumn(id: PercentRate, header: string): LocationColumn {
  const format = (value?: number) => (value === undefined ? `-` : `${value.toFixed(RATE_DECIMALS[id])}%`)
  return {
    accessorFn: (row) => ratesFor(row)[id],
    id,
    header,
    enableSorting: true,
    sortUndefined: -1,
    cell: (props) => format(props.getValue()),
    meta: { defaultVisible: true, infoTooltip: { title: header, message: METRIC_DEFINITIONS[id] } },
    // Weighted across the visible rows (total ÷ total), not an average of row rates.
    footer: ({ table }) => format(totalCounts(table.getFilteredRowModel().rows.map((row) => row.original))[id]),
  }
}

/** "+3.5 pts", "-12.0 pts", or "-" when either period had no new patient calls. */
function formatChange(value?: number): string {
  if (value === undefined) return `-`
  return `${value > 0 ? `+` : ``}${value.toFixed(1)} pts`
}

function scheduleRateChangeColumn(): LocationColumn {
  return {
    accessorFn: (row) => ratesFor(row).scheduleRateChange,
    id: `scheduleRateChange`,
    header: `Schedule Rate Change`,
    enableSorting: true,
    sortUndefined: -1,
    // A dip colors the cell red rather than the whole row (plan D44).
    cell: (props) => (
      <span className={classNames({ 'text-red-600': (props.getValue() ?? 0) < 0 })}>
        {formatChange(props.getValue())}
      </span>
    ),
    meta: {
      defaultVisible: true,
      infoTooltip: { title: `Schedule Rate Change`, message: METRIC_DEFINITIONS.scheduleRateChange },
    },
    footer: ({ table }) =>
      formatChange(totalCounts(table.getFilteredRowModel().rows.map((row) => row.original)).scheduleRateChange),
  }
}

export function voiceAgentOpsLocationColumns({
  showWorkspace,
  onLocationClick,
}: {
  /** Org-level view spans workspaces, so it names each location's workspace. */
  showWorkspace: boolean
  onLocationClick: (row: VoiceAgentOpsLocationRow) => void
}): LocationColumn[] {
  return [
    ...locationColumns(showWorkspace, onLocationClick, `Total`),
    countColumn(`aiCalls`, `AI Calls`),
    countColumn(`newPatientCalls`, `New Patient Calls`),
    countColumn(`appointmentsBooked`, `Appointments Booked`),
    rateColumn(`scheduleRate`, `Schedule Rate`),
    rateColumn(`previousScheduleRate`, `Previous Schedule Rate`),
    scheduleRateChangeColumn(),
    countColumn(`appointmentsCompleted`, `Appointments Completed`),
    countColumn(`toolErrorCalls`, TOOL_FLAG_METRIC_LABEL.toolError),
    countColumn(`toolTimeoutCalls`, TOOL_FLAG_METRIC_LABEL.toolTimeout),
    countColumn(`toolDuplicateCalls`, TOOL_FLAG_METRIC_LABEL.toolDuplicate),
    rateColumn(`failureRate`, `Failure Rate`),
    countColumn(`gptFailedCalls`, `GPT Failures`),
    countColumn(`elevenLabsFailedCalls`, `ElevenLabs Failures`),
    {
      accessorFn: (row) => row.isAIAgentLive,
      id: `agentLive`,
      header: `Agent Live`,
      enableSorting: true,
      cell: (props) => <Badge label={props.getValue() ? `Live` : `Off`} color={props.getValue() ? `green` : `gray`} />,
      meta: { defaultVisible: true },
    },
    {
      accessorFn: (row) => row.lastAICallDate,
      id: `lastAICall`,
      header: `Last AI Call`,
      enableSorting: true,
      sortUndefined: -1,
      cell: (props) => <DateComponent value={props.getValue()} dateOnly />,
      meta: { defaultVisible: true },
    },
  ]
}

/** Dipping Schedule Rates: current vs previous period per dipping location. */
export function voiceAgentOpsDippingColumns({
  showWorkspace,
  onLocationClick,
}: {
  showWorkspace: boolean
  onLocationClick: (row: VoiceAgentOpsLocationRow) => void
}): LocationColumn[] {
  return [
    ...locationColumns(showWorkspace, onLocationClick),
    rateColumn(`scheduleRate`, `Schedule Rate`),
    rateColumn(`previousScheduleRate`, `Previous Schedule Rate`),
    scheduleRateChangeColumn(),
    countColumn(`newPatientCalls`, `New Patient Calls`),
    countColumn(`previousNewPatientCalls`, `Previous New Patient Calls`),
  ]
}

type OpsCallColumnsOptions = { baseUrl: string; onCallClick: (call: OpsCall) => void }

/**
 * An Ops call list: the Activities table's own columns, so it reads like the Call Log,
 * with one list-specific column after Location, plus Duration. Opening a row opens the
 * AI Call Details drawer rather than the activity drawer.
 */
function opsCallColumns({ baseUrl, onCallClick }: OpsCallColumnsOptions, detailColumn: CallColumn): CallColumn[] {
  // The rows are the calls themselves, so the Activities table's handler receives an OpsCall.
  // No Staff column is used, so the owner-update callback is never called.
  const activityColumns = keyBy(
    defaultColumns(
      (row: ActivityJson) => onCallClick(row as OpsCall),
      false,
      () => {},
      baseUrl,
      false,
    ) as CallColumn[],
    `id`,
  )

  return compact([
    activityColumns.activityDate,
    activityColumns.locationId,
    detailColumn,
    activityColumns.contacts,
    activityColumns.nameFromActivity,
    activityColumns.phoneFromActivity,
    {
      accessorFn: (row) => row.durationSeconds,
      id: `duration`,
      header: `Duration`,
      enableSorting: true,
      cell: (props) => <MetricCell value={props.getValue()} type="time" />,
      meta: { defaultVisible: true },
    },
    activityColumns.tags,
  ])
}

/** The first badge, then a "+N" chip for the rest (as the Tags column does), so a cell never stacks badges. */
function BadgesCell({ badges }: { badges: { label: string; color: BadgeColor }[] }) {
  const [first, ...rest] = badges
  return (
    <div className="flex items-center gap-1 overflow-hidden whitespace-nowrap px-2">
      {first && <Badge label={first.label} color={first.color} />}
      <OverflowCountChip items={rest.map((badge) => badge.label)} />
    </div>
  )
}

export function toolFailureCallColumns(options: OpsCallColumnsOptions): CallColumn[] {
  return opsCallColumns(options, {
    accessorFn: (row) => row.toolFlags.map((flag) => TOOL_FLAG_LABEL[flag]).join(`, `),
    id: `errorType`,
    header: `Error Type`,
    enableSorting: true,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) => (
        <BadgesCell
          badges={(row?.toolFlags ?? []).map((flag) => ({
            label: TOOL_FLAG_LABEL[flag],
            color: TOOL_FLAG_COLOR[flag],
          }))}
        />
      ),
    },
  })
}

export function callFailureColumns(options: OpsCallColumnsOptions): CallColumn[] {
  return opsCallColumns(options, {
    accessorFn: (row) => row.failureTypes.map((type) => CALL_FAILURE_LABEL[type]).join(`, `),
    id: `failureType`,
    header: `Failure Type`,
    enableSorting: true,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) => (
        <BadgesCell
          badges={(row?.failureTypes ?? []).map((type) => ({
            label: CALL_FAILURE_LABEL[type],
            color: CALL_FAILURE_GROUP_COLOR[CALL_FAILURE_GROUP[type]],
          }))}
        />
      ),
    },
  })
}

export function unbookedCallColumns(options: OpsCallColumnsOptions): CallColumn[] {
  return opsCallColumns(options, {
    accessorFn: (row) => noBookingReasonLabel(row.noBookingReason),
    id: `noBookingReason`,
    header: `Reason`,
    enableSorting: true,
    meta: { defaultVisible: true },
  })
}
